import React from 'react';
import { useTranslation } from 'react-i18next';
import { PieChart as PieIcon } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const COLORS = ['#4ABDBD', '#6366F1', '#F59E0B', '#EF4444', '#10B981', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316', '#64748B'];
const money = (n) => `€${Number(n || 0).toLocaleString()}`;

// Admin: share of total practice revenue per doctor (donut + legend with %).
// Reuses the leaderboard payload (name + revenue) — no extra backend call.
const RevenueByDoctor = ({ leaderboard, loading }) => {
  const { t } = useTranslation('dashboard');
  const rows = Array.isArray(leaderboard?.data) ? leaderboard.data.filter((r) => Number(r.revenue) > 0) : [];
  const total = rows.reduce((s, r) => s + (Number(r.revenue) || 0), 0);
  const data = rows.map((r, i) => ({ name: r.name, value: Number(r.revenue) || 0, color: COLORS[i % COLORS.length] }));

  return (
    <div className="bg-white w-full rounded-xl p-4 border border-gray-200/50 shadow-md" data-testid="admin-revenue-by-doctor">
      <h3 className="text-base sm:text-lg font-bold text-gray-900 flex items-center mb-3">
        <PieIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-[#4ABDBD]" />
        {t('dashboard.revenue_by_doctor', 'Revenue contribution by doctor')}
      </h3>
      {loading ? (
        <p className="text-sm text-gray-400 py-8 text-center">{t('dashboard.loading', 'Loading...')}</p>
      ) : data.length === 0 ? (
        <p className="text-sm text-gray-400 py-8 text-center">{t('dashboard.no_data', 'No data for this period')}</p>
      ) : (
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="w-40 h-40 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2} stroke="none">
                  {data.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip formatter={(v) => money(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 w-full space-y-1.5 max-h-40 overflow-y-auto custom-scrollbar">
            {data.map((d, i) => (
              <div key={i} className="flex items-center justify-between text-sm" data-testid={`revenue-doctor-row-${i}`}>
                <span className="flex items-center gap-2 min-w-0">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.color }} />
                  <span className="truncate text-gray-700">{d.name}</span>
                </span>
                <span className="flex items-center gap-3 shrink-0 ml-2">
                  <span className="text-gray-400 text-xs">{money(d.value)}</span>
                  <span className="font-semibold text-gray-800 w-9 text-right">{total > 0 ? Math.round((d.value / total) * 100) : 0}%</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueByDoctor;
